import { safeParse, union } from 'valibot';
import {
  AlbumItem,
  AlbumItemSchema,
  ArtWorkAttrSchema,
  PlaylistItem,
  PlaylistItemSchema,
  StationItem,
  StationItemSchema,
  TrackItem,
} from './apiSchema.ts';
import { L, toMediaType } from './utils.ts';

/**
 * Convert a MusicKit.MediaItem object to a TrackItem.
 * @param {MusicKit.MediaItem} item The MusicKit media item to convert.
 * @returns {TrackItem | null} The converted TrackItem or null if the item is invalid.
 */
export function queueToTrackItem(
  item: MusicKit.MediaItem,
): TrackItem | null {
  if (!item) return null;
  const {
    type,
    id,
    title,
    artistName,
    artwork: orgArtwork,
    attributes,
    contentRating,
  } = item;

  const artwork = ((artwork: MusicKit.Artwork | undefined) => {
    if (!artwork) return undefined;
    const parsedArtwork = safeParse(ArtWorkAttrSchema, artwork);
    if (!parsedArtwork.success) {
      L.error(
        '[getCurrentPlayerStatus] Invalid artwork data:',
        JSON.stringify(parsedArtwork.issues, null, 2),
      );
      return undefined;
    }
    return parsedArtwork.output;
  })(orgArtwork);

  return {
    type: toMediaType(type, 'NativeType'),
    id,
    name: title,
    artistName,
    artwork,
    isLibrary: attributes?.playParams?.isLibrary ?? false,
    contentRating,
  } as TrackItem;
}

/**
 * Convert a MusicKit.MediaItem container to an AlbumItem or PlaylistItem.
 * @param {MusicKit.MediaItem} item The MusicKit media item to convert.
 * @returns {AlbumItem | PlaylistItem | null} The converted AlbumItem or PlaylistItem, or null if the item is invalid.
 */
export function toContainerMediaItem(
  item: MusicKit.MediaItem,
): AlbumItem | PlaylistItem | StationItem | null {
  if (!item || !item.container) return null;

  if (item.container.type === undefined) {
    // If the container is a song, we return null as it doesn't match AlbumItem or PlaylistItem.
    return null;
  }

  const parsedItem = safeParse(
    union([AlbumItemSchema, PlaylistItemSchema, StationItemSchema]),
    Object(item.container),
  );
  if (!parsedItem.success) {
    L.error(
      '[toContainerObject] Invalid container data',
    );
    return null;
  }

  return parsedItem.output;
}
export const filterUnsupportedItems = (item: MusicKit.MediaItem) => {
  // Filter out unsupported items like music videos and live stations
  return item.type !== 'musicVideo' &&
    !(item.type === 'radioStation' &&
      item.attributes?.playParam?.format === 'stream' &&
      item.attributes?.playParam?.hasDrm === true);
};

export const getNextPlayableIndex = (
  items: MusicKit.MediaItem[],
  currentIndex: number,
): number => {
  for (let i = currentIndex + 1; i < items.length; i++) {
    if (filterUnsupportedItems(items[i])) {
      return i;
    }
  }
  return -1; // No next playable item
};

export const isPaused = (player: MusicKit.MusicKitInstance): boolean => {
  return player.playbackState === MusicKit.PlaybackStates.paused &&
    !player.queueIsEmpty &&
    !player.isPlaying;
};
